import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Avatar, Divider,
} from '@mui/material';
import {
  AccessTime, CheckCircle, Cancel, Edit, Schedule, TrendingUp,
  Person, CalendarToday,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string; emoji: string }> = {
  present:        { color: 'success', label: 'Present',     emoji: '✅' },
  absent:         { color: 'error',   label: 'Absent',      emoji: '❌' },
  late:           { color: 'warning', label: 'Late',        emoji: '⏰' },
  half_day:       { color: 'info',    label: 'Half Day',    emoji: '🌓' },
  on_leave:       { color: 'primary', label: 'On Leave',    emoji: '🌴' },
  work_from_home: { color: 'secondary', label: 'WFH',       emoji: '🏠' },
  holiday:        { color: 'default', label: 'Holiday',     emoji: '🎉' },
};

function fmtTime(d: string | Date | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-HK', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtMins(m: number | null) {
  if (!m) return '—';
  const h = Math.floor(m / 60), min = m % 60;
  return h ? `${h}h ${min}m` : `${min}m`;
}

interface AttLog { id: string; employeeId: string; date: string; clockIn?: string; clockOut?: string; workedMinutes?: number; lateMinutes?: number; overtimeMinutes?: number; status: string; clockMethod: string; notes?: string; isManualEdit: boolean; employee?: { firstName: string; lastName: string; employeeId: string }; }
interface Summary { totalDays: number; presentDays: number; absentDays: number; lateDays: number; halfDays: number; leaveDays: number; wfhDays: number; totalWorkedHours: number; totalLateMinutes: number; totalOvertimeHours: number; logs: AttLog[]; }

function StatusChip({ status }: { status: string }) {
  const cfg = STATUS_CFG[status] ?? { color: 'default', label: status, emoji: '?' };
  return <Chip label={`${cfg.emoji} ${cfg.label}`} size="small" color={cfg.color} />;
}

export default function TimeAttendance() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = isHR || user?.role === 'manager';

  const today = new Date().toISOString().slice(0, 10);
  const curMonth = today.slice(0, 7);
  const [tab, setTab] = useState(0);
  const [todayLog, setTodayLog] = useState<AttLog | null>(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [teamLogs, setTeamLogs] = useState<AttLog[]>([]);
  const [teamDate, setTeamDate] = useState(today);
  const [month, setMonth] = useState(curMonth);
  const [myLogs, setMyLogs] = useState<AttLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualDialog, setManualDialog] = useState<Partial<AttLog> | null>(null);
  const [liveTime, setLiveTime] = useState(new Date());

  useEffect(() => { const t = setInterval(() => setLiveTime(new Date()), 1000); return () => clearInterval(t); }, []);

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [td, sm, ml] = await Promise.all([
        apiFetch('/api/attendance/today'),
        apiFetch(`/api/attendance/my/summary?month=${month}`),
        apiFetch(`/api/attendance/my?from=${month}-01&to=${today}`),
      ]);
      setTodayLog(td || null);
      setSummary(sm);
      setMyLogs(ml || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [month, today]);

  const loadTeam = useCallback(async () => {
    if (!isManager) return;
    try {
      const r = await apiFetch(`/api/attendance/team?date=${teamDate}`);
      setTeamLogs(r.logs || []);
    } catch (e: any) { setError(e.message); }
  }, [isManager, teamDate]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadTeam(); }, [loadTeam]);

  const clockIn = async () => {
    setClockLoading(true);
    try { const r = await apiFetch('/api/attendance/clock-in', { method: 'POST', body: JSON.stringify({}) }); setTodayLog(r); loadAll(); }
    catch (e: any) { setError(e.message); }
    setClockLoading(false);
  };

  const clockOut = async () => {
    setClockLoading(true);
    try { const r = await apiFetch('/api/attendance/clock-out', { method: 'POST', body: JSON.stringify({}) }); setTodayLog(r); loadAll(); }
    catch (e: any) { setError(e.message); }
    setClockLoading(false);
  };

  const saveManual = async () => {
    if (!manualDialog) return;
    try {
      await apiFetch('/api/attendance/manual', { method: 'POST', body: JSON.stringify(manualDialog) });
      setManualDialog(null); loadAll(); loadTeam();
    } catch (e: any) { setError(e.message); }
  };

  const canClockIn  = !todayLog?.clockIn;
  const canClockOut = !!todayLog?.clockIn && !todayLog?.clockOut;
  const workedSoFar = todayLog?.clockIn && !todayLog?.clockOut
    ? Math.round((liveTime.getTime() - new Date(todayLog.clockIn).getTime()) / 60000)
    : todayLog?.workedMinutes ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AccessTime sx={{ fontSize: 32, color: '#1a237e' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Time & Attendance</Typography>
          <Typography variant="body2" color="text.secondary">Track your hours and daily attendance</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── CLOCK WIDGET ── */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', color: '#fff' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {liveTime.toLocaleTimeString('en-HK', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
              </Typography>
              <Typography sx={{ opacity: 0.8 }}>
                {liveTime.toLocaleDateString('en-HK', { weekday: 'long', day: 'numeric', month: 'long' })}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CheckCircle sx={{ fontSize: 18, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Clock In</Typography>
                  <Typography fontWeight={700}>{fmtTime(todayLog?.clockIn)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Cancel sx={{ fontSize: 18, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Clock Out</Typography>
                  <Typography fontWeight={700}>{fmtTime(todayLog?.clockOut)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Schedule sx={{ fontSize: 18, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Worked</Typography>
                  <Typography fontWeight={700}>{fmtMins(workedSoFar)}{!todayLog?.clockOut && todayLog?.clockIn ? ' ⏱' : ''}</Typography>
                </Box>
                {todayLog?.status && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={STATUS_CFG[todayLog.status]?.emoji + ' ' + STATUS_CFG[todayLog.status]?.label} size="small"
                      sx={{ color: '#fff', borderColor: '#fff', border: '1px solid' }} />
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!canClockIn || clockLoading}
                  onClick={clockIn}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, fontWeight: 700, fontSize: '1rem' }}
                >
                  ▶ Clock In
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!canClockOut || clockLoading}
                  onClick={clockOut}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, fontWeight: 700, fontSize: '1rem' }}
                >
                  ■ Clock Out
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Month Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Present',    value: summary.presentDays,       color: '#2e7d32' },
            { label: 'Absent',     value: summary.absentDays,        color: '#c62828' },
            { label: 'Late Days',  value: summary.lateDays,          color: '#e65100' },
            { label: 'WFH Days',   value: summary.wfhDays,           color: '#4a148c' },
            { label: 'Hrs Worked', value: summary.totalWorkedHours,  color: '#1a237e' },
            { label: 'OT Hours',   value: summary.totalOvertimeHours, color: '#006064' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="My Log" />
          {isManager && <Tab label="Team View" />}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── MY LOG ── */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField label="Month" type="month" size="small" InputLabelProps={{ shrink: true }}
              value={month} onChange={e => setMonth(e.target.value)} />
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Clock In</b></TableCell>
                  <TableCell><b>Clock Out</b></TableCell>
                  <TableCell><b>Worked</b></TableCell>
                  <TableCell><b>Late</b></TableCell>
                  <TableCell><b>OT</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myLogs.map(l => (
                  <TableRow key={l.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      {new Date(l.date + 'T00:00:00').toLocaleDateString('en-HK', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {l.isManualEdit && <Chip label="edited" size="small" sx={{ ml: 1, fontSize: '0.6rem' }} />}
                    </TableCell>
                    <TableCell><StatusChip status={l.status} /></TableCell>
                    <TableCell>{fmtTime(l.clockIn)}</TableCell>
                    <TableCell>{fmtTime(l.clockOut)}</TableCell>
                    <TableCell>{fmtMins(l.workedMinutes ?? null)}</TableCell>
                    <TableCell>{l.lateMinutes ? <Typography color="warning.main" variant="body2">{l.lateMinutes}m</Typography> : '—'}</TableCell>
                    <TableCell>{l.overtimeMinutes ? <Typography color="success.main" variant="body2">+{fmtMins(l.overtimeMinutes)}</Typography> : '—'}</TableCell>
                  </TableRow>
                ))}
                {myLogs.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No records this month.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── TEAM VIEW (HR/Manager) ── */}
      {tab === 1 && isManager && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField label="Date" type="date" size="small" InputLabelProps={{ shrink: true }}
              value={teamDate} onChange={e => setTeamDate(e.target.value)} />
            {isHR && (
              <Button variant="outlined" size="small" startIcon={<Edit />}
                onClick={() => setManualDialog({ date: teamDate, clockMethod: 'manual', status: 'present' })}>
                Manual Entry
              </Button>
            )}
          </Box>

          {/* Status summary pills */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {Object.entries(
              teamLogs.reduce((acc: Record<string, number>, l) => { acc[l.status] = (acc[l.status] ?? 0) + 1; return acc; }, {})
            ).map(([status, count]) => (
              <Chip key={status} label={`${STATUS_CFG[status]?.emoji ?? ''} ${STATUS_CFG[status]?.label ?? status}: ${count}`}
                size="small" color={STATUS_CFG[status]?.color} />
            ))}
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Employee</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>In</b></TableCell>
                  <TableCell><b>Out</b></TableCell>
                  <TableCell><b>Worked</b></TableCell>
                  <TableCell><b>Late</b></TableCell>
                  <TableCell><b>OT</b></TableCell>
                  {isHR && <TableCell><b>Action</b></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {teamLogs.map(l => (
                  <TableRow key={l.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#1a237e', fontSize: '0.7rem' }}>
                          {l.employee?.firstName?.[0]}{l.employee?.lastName?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{l.employee?.firstName} {l.employee?.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{l.employee?.employeeId}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><StatusChip status={l.status} /></TableCell>
                    <TableCell>{fmtTime(l.clockIn)}</TableCell>
                    <TableCell>{fmtTime(l.clockOut)}</TableCell>
                    <TableCell>{fmtMins(l.workedMinutes ?? null)}</TableCell>
                    <TableCell>{l.lateMinutes ? <Typography color="warning.main" variant="body2">{l.lateMinutes}m</Typography> : '—'}</TableCell>
                    <TableCell>{l.overtimeMinutes ? <Typography color="success.main" variant="body2">+{fmtMins(l.overtimeMinutes)}</Typography> : '—'}</TableCell>
                    {isHR && (
                      <TableCell>
                        <IconButton size="small" onClick={() => setManualDialog({ ...l })}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {teamLogs.length === 0 && (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No records for this date.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── MANUAL ENTRY DIALOG ── */}
      <Dialog open={!!manualDialog} onClose={() => setManualDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Manual Attendance Entry</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Employee ID" fullWidth value={manualDialog?.employeeId ?? ''}
              onChange={e => setManualDialog(p => ({ ...p, employeeId: e.target.value }))} />
            <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={manualDialog?.date ?? ''} onChange={e => setManualDialog(p => ({ ...p, date: e.target.value }))} />
            <TextField label="Status" select fullWidth value={manualDialog?.status ?? 'present'}
              onChange={e => setManualDialog(p => ({ ...p, status: e.target.value }))}>
              {Object.entries(STATUS_CFG).map(([v, c]) => (
                <MenuItem key={v} value={v}>{c.emoji} {c.label}</MenuItem>
              ))}
            </TextField>
            <TextField label="Clock In" type="time" fullWidth InputLabelProps={{ shrink: true }}
              value={manualDialog?.clockIn ? new Date(manualDialog.clockIn as any).toTimeString().slice(0, 5) : ''}
              onChange={e => {
                const [h, m] = e.target.value.split(':');
                const d = new Date(manualDialog?.date ?? today + 'T00:00:00');
                d.setHours(+h, +m);
                setManualDialog(p => ({ ...p, clockIn: d.toISOString() as any }));
              }} />
            <TextField label="Clock Out" type="time" fullWidth InputLabelProps={{ shrink: true }}
              value={manualDialog?.clockOut ? new Date(manualDialog.clockOut as any).toTimeString().slice(0, 5) : ''}
              onChange={e => {
                const [h, m] = e.target.value.split(':');
                const d = new Date(manualDialog?.date ?? today + 'T00:00:00');
                d.setHours(+h, +m);
                setManualDialog(p => ({ ...p, clockOut: d.toISOString() as any }));
              }} />
            <TextField label="Notes" fullWidth multiline rows={2} value={manualDialog?.notes ?? ''}
              onChange={e => setManualDialog(p => ({ ...p, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveManual}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
